import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Star } from "@phosphor-icons/react";
import { C, R } from "@/brand/tokens";
import { SiteHeader6 } from "@/components/layout/SiteHeader6";
import { SiteFooter6 } from "@/components/layout/SiteFooter6";
import { InteriorHero } from "@/components/common/InteriorHero";
import { Box6 } from "@/components/common/Box6";
import { Pill6 } from "@/components/common/Pill6";
import { CtaBand6 } from "@/components/common/CtaBand6";
import { resourceCategories6 } from "@/brand/contentF6";
import { fetchResources } from "@/lib/resourcesApi";
import { usePageMeta } from "@/lib/usePageMeta";

const RESOURCES_INTRO =
  "Insights for durable questions, guides and tools you can use today, and anonymized field notes from real engagements — written for the leaders who carry the people side of the organization.";

function ResourceCard({ r, index }) {
  return (
    <Box6
      as={Link}
      to={`/resources/${r.slug}`}
      tone="white"
      radius="resource"
      className="group flex flex-col p-7 lg:p-8 transition-colors"
      style={{ minHeight: 280, ...(r.featured ? { border: `1px solid ${C.accentMetal}` } : {}) }}
      data-testid={`resource-item-${index}`}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.surfacePrimary)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = r.featured ? C.accentMetal : C.brassLine)}
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <span className="font-inter tabular-nums text-[10px] uppercase font-semibold" style={{ color: C.inkOnLightSoft, letterSpacing: "0.16em" }}>
          {r.publish_date}
        </span>
        {r.featured && (
          <span className="inline-flex items-center gap-1.5 font-inter text-[9.5px] uppercase font-semibold" style={{ color: C.accentMetal, letterSpacing: "0.18em" }}>
            <Star size={11} weight="fill" />
            Featured
          </span>
        )}
      </div>
      <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.2rem, 1.6vw, 1.4rem)", fontWeight: 400, lineHeight: 1.2 }}>
        {r.title}
      </h3>
      <p className="mt-4 font-inter leading-[1.65] grow" style={{ color: C.inkOnLight, fontSize: "0.9rem" }}>
        {r.summary}
      </p>
      <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: `1px solid ${C.brassSoft}` }}>
        <span className="font-inter text-[10px] uppercase font-semibold" style={{ color: C.inkOnLightSoft, letterSpacing: "0.16em" }}>
          {r.author}
        </span>
        <ArrowRight size={12} weight="bold" className="transition-transform group-hover:translate-x-1 shrink-0" style={{ color: C.accentFunctional }} />
      </div>
    </Box6>
  );
}

/**
 * ResourcesPage — single landing page with three groupings:
 * Insights · Guides & Tools · Field Notes. Filter narrows to one group.
 */
export default function ResourcesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("category") || "all";
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    fetchResources()
      .then((r) => {
        if (active) {
          setItems(r);
          setLoaded(true);
        }
      })
      .catch(() => setLoaded(true));
    return () => {
      active = false;
    };
  }, []);

  const groups = resourceCategories6
    .filter((c) => activeCat === "all" || c.id === activeCat)
    .map((c) => ({ ...c, items: items.filter((r) => r.category === c.id) }));

  const activeMeta = resourceCategories6.find((c) => c.id === activeCat);
  usePageMeta({
    title: activeMeta ? `${activeMeta.label} · Resources` : "Resources",
    description: activeMeta ? `${activeMeta.label} from Faith 1st Consulting — ${activeMeta.dek}` : RESOURCES_INTRO,
  });

  let running = 0;

  return (
    <main data-testid="resources-page" style={{ background: C.surfaceLight, color: C.textOnLight }}>
      <SiteHeader6 />

      <InteriorHero
        breadcrumb={[{ label: "Faith 1st Consulting", href: "/" }, { label: "Resources" }]}
        eyebrow="Resources"
        title="A working library for leaders."
        intro={RESOURCES_INTRO}
      />

      <section data-testid="resources-body" style={{ background: C.surfaceLight2 }}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="resources-filter">
            {[{ id: "all", label: "All" }, ...resourceCategories6].map((c) => {
              const active = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`filter-${c.id}`}
                  onClick={() => (c.id === "all" ? setSearchParams({}) : setSearchParams({ category: c.id }))}
                  className="px-5 py-2.5 font-inter text-[11px] uppercase font-semibold transition-colors"
                  style={{
                    letterSpacing: "0.2em",
                    borderRadius: R.button,
                    background: active ? C.surfacePrimary : C.surfaceLight,
                    color: active ? C.textOnDark : C.textOnLight,
                    border: `1px solid ${active ? C.surfacePrimary : C.brassLine}`,
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-16" data-testid="resources-grid">
            {groups.map((g) => {
              const start = running;
              running += g.items.length;
              return (
                <div key={g.id} data-testid={`resource-group-${g.id}`}>
                  <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-4" style={{ borderBottom: `1px solid ${C.brassLine}` }}>
                    <div>
                      <Pill6 tone="blue">{g.label}</Pill6>
                      <h2 className="mt-4 font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.5rem, 2.4vw, 2rem)", fontWeight: 400, lineHeight: 1.1 }}>
                        {g.dek}
                      </h2>
                    </div>
                    <span className="font-inter tabular-nums text-[10.5px] uppercase font-semibold" style={{ color: C.inkOnLightSoft, letterSpacing: "0.2em" }}>
                      {loaded ? `${g.items.length} ${g.items.length === 1 ? "item" : "items"}` : ""}
                    </span>
                  </div>
                  {loaded && g.items.length === 0 ? (
                    <p data-testid="resources-empty" className="font-cormorant italic py-6" style={{ color: C.inkOnLightSoft, fontSize: "1.15rem" }}>
                      Nothing published in this category yet — check back soon.
                    </p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {g.items.map((r, i) => (
                        <ResourceCard key={r.id} r={r} index={start + i} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand6 eyebrow="Reading is a start" headline="A conversation moves the situation." testId="resources-cta" />
      <SiteFooter6 />
    </main>
  );
}
